import { memo, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RivetsApiError } from "@rivets/core";
import { client } from "@/lib/api";
import { AvatarChip } from "@/components/AvatarChip";
import { memberName, useOrgContext } from "@/lib/OrgContext";
import { perfMeasure } from "@/lib/perf";
import { resizeTextarea } from "./autoGrow";
import type { ChatBlock } from "./foldChatEvents";
import { deliverPendingSend, discardPendingSend, type PendingSend } from "./pendingSends";

type QueuedBlock = Extract<ChatBlock, { kind: "user" }>;

/**
 * Messages the agent has not picked up yet, stacked just above the composer.
 *
 * They stay out of the transcript until the agent takes them, so the turn the
 * agent is still finishing cannot bury them, and each one stays in its author's
 * hands: edit it, delete it, or send it now — which interrupts the agent's
 * current turn so this one starts next. A prioritized message moves to the top
 * of the stack and reads "Sending…" until its turn opens.
 *
 * A message queued while the agent is busy lands here the moment Send is
 * pressed (`sends`), not in the transcript: it rides at the foot of the queue
 * with its actions held until the server has it, and its echo takes its place.
 *
 * In the task chat the caller passes only what the reader can act on (see
 * `waitingMessages.ts`): their own messages, in queue mode, not yet sent now.
 * A steer, or a teammate's message, is drawn in the transcript instead.
 *
 * Shared by the task chat and the assistant drawer; the two differ only in
 * who may act on a row and in what part of a message is the author's to show.
 */
export function QueuedMessages({
  chatId,
  messages,
  sends = NO_SENDS,
  canEdit,
  canManage,
  bodyOf = (block) => block.text,
  className = "",
}: {
  chatId: string;
  messages: QueuedBlock[];
  /** This device's sends into the busy agent that the server has not echoed. */
  sends?: PendingSend[];
  /** Whether the viewer may rewrite this message's text. */
  canEdit: (block: QueuedBlock) => boolean;
  /** Whether the viewer may delete this message or send it now. */
  canManage: (block: QueuedBlock) => boolean;
  /** The part of the message its author wrote, which is all a row shows. */
  bodyOf?: (block: QueuedBlock) => string;
  className?: string;
}) {
  const count = messages.length + sends.length;
  if (count === 0) return null;
  return (
    <section className={`queued-messages${className ? ` ${className}` : ""}`} aria-label="Queued messages">
      <div className="queued-messages-header">
        <span className="undelivered-dot" aria-hidden="true" />
        {count} queued
      </div>
      <ul className="queued-messages-list">
        {messages.map((block) => (
          <QueuedMessageRow
            key={block.id}
            block={block}
            chatId={chatId}
            body={bodyOf(block)}
            editable={canEdit(block)}
            manageable={canManage(block)}
          />
        ))}
        {sends.map((send) => <PendingQueuedRow key={send.clientRequestId} send={send} />)}
      </ul>
    </section>
  );
}

const NO_SENDS: PendingSend[] = [];

/** A send on its way to the queue. Drawn as the row it is about to become, saying
 * whether it is still sending or the server has it; its actions arrive with the
 * queued message they act on. A failed one offers Retry and Discard instead. */
const PendingQueuedRow = memo(function PendingQueuedRow({ send }: { send: PendingSend }) {
  const queryClient = useQueryClient();
  const { membersById } = useOrgContext();
  const userId = send.userId ?? "";
  const member = membersById.get(userId);
  const failed = send.state === "failed";
  useEffect(() => {
    perfMeasure("rivets:send→bubble", `rivets:send:${send.clientRequestId}`);
  }, [send.clientRequestId]);
  const attachmentCount = send.attachments?.length ?? 0;
  return (
    <li className={`queued-message queued-message-pending ${send.state}`} data-pending-send={send.clientRequestId}>
      <AvatarChip id={userId} name={memberName(membersById, userId)} image={member?.image} avatarUrls={member?.avatarUrls} size={16} />
      <div className="queued-message-main">
        <div className="queued-message-text" title={send.text}>
          {send.text}
          {attachmentCount > 0 ? (
            <span className="queued-message-attachments">
              {" "}+{attachmentCount} {attachmentCount === 1 ? "attachment" : "attachments"}
            </span>
          ) : null}
        </div>
        {failed ? (
          <div className="queued-message-error chat-block-send-failed" role="alert">
            <span>{send.error ?? "Could not send this message."}</span>
            <button type="button" className="chat-send-action" onClick={() => void deliverPendingSend(queryClient, send)}>Retry</button>
            <button type="button" className="chat-send-action" onClick={() => discardPendingSend(send)}>Discard</button>
          </div>
        ) : null}
      </div>
      {failed ? null : (
        // On its way, or taken by the server and waiting for its place in
        // the queue — two looks, so a held message never reads as unsent.
        <span className="queued-message-status" aria-live="polite">
          {send.state === "sending" ? <><span className="spinner chat-send-spinner" aria-hidden="true" />Sending…</> : "Sent"}
        </span>
      )}
    </li>
  );
});

const QueuedMessageRow = memo(function QueuedMessageRow({
  block,
  chatId,
  body,
  editable,
  manageable,
}: {
  block: QueuedBlock;
  chatId: string;
  body: string;
  editable: boolean;
  manageable: boolean;
}) {
  const { membersById } = useOrgContext();
  const member = membersById.get(block.userId);
  const name = memberName(membersById, block.userId);
  const [draft, setDraft] = useState<string | null>(null);
  const editing = draft !== null;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const revise = useMutation({
    mutationFn: (text: string) => client.reviseMessage(chatId, block.turnId, { text }),
    onSuccess: () => setDraft(null),
  });
  // Neither resolves the row: it leaves or moves when the chat's own event for
  // it arrives, so every client watching the chat sees the same queue.
  const withdraw = useMutation({ mutationFn: () => client.withdrawMessage(chatId, block.turnId) });
  const sendNow = useMutation({ mutationFn: () => client.sendMessageNow(chatId, block.turnId) });
  const sending = block.sendingNow === true || sendNow.isPending;
  const busy = revise.isPending || withdraw.isPending || sending;
  const error = revise.error ?? withdraw.error ?? sendNow.error;

  useLayoutEffect(() => {
    if (!editing) return;
    resizeTextarea(textareaRef.current, { minLines: 1, maxLines: 10 });
  }, [editing, draft]);

  useEffect(() => {
    if (!editing) return;
    const element = textareaRef.current;
    if (!element) return;
    element.focus();
    element.setSelectionRange(element.value.length, element.value.length);
    // Only on open: re-running would fight the caret on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  function startEditing() {
    revise.reset();
    setDraft(body);
  }

  function save() {
    const text = (draft ?? "").trim();
    if (!text || revise.isPending) return;
    if (text === body) { setDraft(null); return; }
    revise.mutate(text);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Escape") { event.preventDefault(); setDraft(null); return; }
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") { event.preventDefault(); save(); }
  }

  const attachmentCount = block.attachments?.length ?? 0;
  return (
    <li className={`queued-message${editing ? " editing" : ""}${sending ? " sending" : ""}`}>
      <AvatarChip id={block.userId} name={name} image={member?.image} avatarUrls={member?.avatarUrls} size={16} />
      <div className="queued-message-main">
        {editing ? (
          <>
            <textarea
              ref={textareaRef}
              className="undelivered-editor queued-message-editor"
              aria-label="Edit queued message"
              value={draft ?? ""}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <div className="undelivered-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft(null)} disabled={revise.isPending}>Cancel</button>
              <button type="button" className="btn btn-sm" onClick={save} disabled={revise.isPending || !(draft ?? "").trim()}>
                {revise.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        ) : (
          <div className="queued-message-text" title={body}>
            {body}
            {attachmentCount > 0 ? (
              <span className="queued-message-attachments">
                {" "}+{attachmentCount} {attachmentCount === 1 ? "attachment" : "attachments"}
              </span>
            ) : null}
          </div>
        )}
        {error ? (
          <div className="queued-message-error" role="alert">
            {error instanceof RivetsApiError ? error.message : errorFallback(error === revise.error ? "edit" : error === withdraw.error ? "delete" : "send")}
          </div>
        ) : null}
      </div>
      {sending ? (
        <span className="queued-message-status" aria-live="polite">Sending…</span>
      ) : !editing && (editable || manageable) ? (
        <div className="queued-message-actions">
          {editable ? (
            <QueuedAction label="Edit message" onClick={startEditing} disabled={busy}>{EDIT_GLYPH}</QueuedAction>
          ) : null}
          {manageable ? (
            <>
              <QueuedAction label={withdraw.isPending ? "Deleting…" : "Delete message"} onClick={() => { sendNow.reset(); withdraw.mutate(); }} disabled={busy}>{DELETE_GLYPH}</QueuedAction>
              <QueuedAction label="Send now" onClick={() => { withdraw.reset(); sendNow.mutate(); }} disabled={busy}>{SEND_NOW_GLYPH}</QueuedAction>
            </>
          ) : null}
        </div>
      ) : null}
    </li>
  );
});

function errorFallback(action: "edit" | "delete" | "send"): string {
  return action === "send" ? "Could not send this message now." : `Could not ${action} this message.`;
}

const EDIT_GLYPH = <path d="M16.5 4.5a2.12 2.12 0 0 1 3 3L8 19l-4 1 1-4z"/>;
const DELETE_GLYPH = <path d="M4 7h16M10 11v6M14 11v6M9 7V4.5h6V7M6 7l1 12.5A1.5 1.5 0 0 0 8.5 21h7a1.5 1.5 0 0 0 1.5-1.5L18 7"/>;
const SEND_NOW_GLYPH = <path d="M12 19V5M6 11l6-6 6 6"/>;

/** An icon-only row action. The glyph is drawn on the same 24px box as the
 * app's navigation icons, and the label rides on `title` and `aria-label`. */
function QueuedAction({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled: boolean; children: ReactNode }) {
  return (
    <button type="button" className="queued-message-action" onClick={onClick} disabled={disabled} title={label} aria-label={label}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
      </svg>
    </button>
  );
}
